import { cx } from "@emotion/css";
import { PopoverBasicWithTrigger } from "@octopusdeploy/design-system-components";
import { PlusIcon } from "@octopusdeploy/design-system-icons";
import { StringHelper } from "@octopusdeploy/design-system-octopus-components";
import { space, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { ActionTemplateParameterResource, ChannelResource, EnvironmentV2Resource, FeedResource, GetTeamsLookupBffResponseTeam, GitRef, GitRefResource, PackageReference, PlatformHubAccountSummary, ProjectSummaryResource, PropertyValueResource, ScopeValues, TemplateParameterResource, VariableSetResource, WorkerPoolResource, } from "@octopusdeploy/octopus-server-client";
import { ControlType, getParametersOfType, isAccountControlType } from "@octopusdeploy/octopus-server-client";
import { noOp } from "@octopusdeploy/utilities";
import * as React from "react";
import { useMemo, useState, useRef } from "react";
import type { StepEditorEvent } from "~/analytics/Analytics";
import { useScopeWidth } from "~/areas/platformHub/processEditor/useScopeWidth";
import type { ParameterValueRow, TemplateValueKey } from "~/areas/platformHub/processTemplates/TemplateParametersTable";
import { createTemplateValueKey, showMultipleValuesAndScopesIndicators, TemplateParameterScopeCell } from "~/areas/platformHub/processTemplates/TemplateParametersTable";
import type { FailedPermissionCheck } from "~/areas/projects/components/Process/ProcessStepsLayoutTypes";
import { isFailedPermissionCheck } from "~/areas/projects/components/Process/ProcessStepsLayoutTypes";
import { ClearOldParameterValuesCallout, createSummaryForParameterValue, defaultScopeValues, getAllVariables, getAccounts, getControlTypeFromParameterValueRow, getUniqueAccountVariableNames, isAccountControlTypeWithoutDefaultValues, isOverrideVariableRow, renderSummaryNodeForParameterValue, templateParameterValueStyles, } from "~/areas/projects/components/Process/ProcessTemplates/ProcessTemplateActionParameterValues";
import type { ProcessIdentifier } from "~/areas/projects/components/Process/types";
import { toTemplatedProjectProcessIdentifier } from "~/areas/projects/components/Process/types";
import type { LoadedLibraryVariableSets } from "~/areas/projects/components/Variables/AllVariables/SharedAllVariablesPage";
import ReadonlyInlineVariableValue from "~/areas/projects/components/Variables/TenantVariables/ReadonlyInlineVariableValue";
import { getVariableTypeFromDisplaySettings } from "~/areas/projects/components/Variables/TenantVariables/getVariableTypeFromDisplaySettings";
import { useProjectContext } from "~/areas/projects/context";
import MissingVariablesIcon from "~/areas/tenants/MissingVariablesIcon/MissingVariablesIcon";
import { isAccountType } from "~/areas/variables/isAccountType";
import { repository } from "~/clientInstance";
import type { PackageSourceItems, WorkerPoolSourceItems } from "~/components/ActionTemplateParameterInput/ActionTemplateParameterInput";
import ActionTemplateParameterInput from "~/components/ActionTemplateParameterInput/ActionTemplateParameterInput";
import { getLabel } from "~/components/ActionTemplateParameterInput/ActionTemplateParameterInputRenderer";
import { type DoBusyTask } from "~/components/DataBaseComponent/index";
import { GroupedDataTable } from "~/components/GroupedDataTable/GroupedDataTable";
import { IconButtonWithTooltip } from "~/components/IconButtonWithTooltip";
import Markdown from "~/components/Markdown/index";
import PopoverWhenFocused from "~/components/PopoverWhenFocused";
import convertPropertyValueResourceToString from "~/components/convertPropertyValueResourceToString";
import { Text } from "~/components/form/index";
import selectOptionsToItems from "~/primitiveComponents/form/Select/Options";
import { getAllProjectActionTemplates } from "~/utils/LoadProjectVariables/loadProjectVariables";
interface ProjectTemplateSourceItems {
    projectId: string;
    variableSet: VariableSetResource | FailedPermissionCheck;
    libraryVariableSets: LoadedLibraryVariableSets[] | FailedPermissionCheck;
    processIdentifier: ProcessIdentifier;
    refreshVariables: (() => Promise<void>) | undefined;
    gitRef: GitRefResource | undefined;
    stepNames: string[];
    availableTargetTags: string[];
    environments: EnvironmentV2Resource[];
    workerPools: WorkerPoolSourceItems;
    packages: PackageSourceItems;
    teams: GetTeamsLookupBffResponseTeam[];
    channels: ChannelResource[];
    projects: ProjectSummaryResource[];
    actionTemplates: ActionTemplateParameterResource[];
}
interface ProjectTemplateParameterValuesProps {
    doBusyTask: DoBusyTask;
    onParameterValueChanged: (parameterName: string, value: PropertyValueResource | undefined) => void;
    parameters: TemplateParameterResource[];
    parameterValues: Record<string, PropertyValueResource | undefined>;
    gitRefResource: GitRefResource | undefined;
    projectVariableSet: VariableSetResource | FailedPermissionCheck;
    libraryVariableSets: LoadedLibraryVariableSets[] | FailedPermissionCheck;
    environments: EnvironmentV2Resource[];
    analyticsStepEditorDispatch: (name: string, event: StepEditorEvent) => void;
    availableWorkerPools: WorkerPoolResource[];
    machineRoles: string[];
    teams: GetTeamsLookupBffResponseTeam[];
    isTenanted: boolean;
    channels: ChannelResource[];
    projects: ProjectSummaryResource[];
    feeds: FeedResource[];
    refreshVariables: () => Promise<void>;
    refreshFeeds: () => Promise<void>;
    platformHubDefaultGitRef: GitRef;
}
export function ProjectTemplateParameterValues({ doBusyTask, onParameterValueChanged, parameters, parameterValues, gitRefResource, projectVariableSet, libraryVariableSets, environments, analyticsStepEditorDispatch, availableWorkerPools, machineRoles, isTenanted, teams, channels, projects, feeds, refreshVariables, refreshFeeds, }: ProjectTemplateParameterValuesProps) {
    const projectContext = useProjectContext();
    const [parameterWorkerPools, setParameterWorkerPools] = useState(availableWorkerPools);
    const platformHubAccountIds = useMemo(() => parameters.filter((p) => isAccountType(getVariableTypeFromDisplaySettings(p.DisplaySettings["Octopus.ControlType"]))).flatMap((p) => p.Values.map((v) => v.Value)), [parameters]);
    const { result: platformHubAccountNames } = useQuery(() => repository.PlatformHubAccounts.getAccountNames(platformHubAccountIds), [platformHubAccountIds], "Get platform hub account names");
    const project = projectContext.state.model;
    /* eslint-disable @typescript-eslint/no-non-null-assertion */
    const templatedProjectIdentifier = useMemo(() => toTemplatedProjectProcessIdentifier(project, project.ProjectTemplateDetails!), [project]);
    const packageReferences: PackageReference[] = getParametersOfType(parameters, ControlType.Package).map((p) => ({
        Name: p.Name,
        PackageId: "",
        FeedId: "",
        AcquisitionLocation: "",
        Properties: {
            PackageParameterName: p.Name,
            SelectionMode: "deferred",
        },
        Id: "",
    }));
    if (!platformHubAccountNames) {
        return null;
    }
    const sourceItems: ProjectTemplateSourceItems = {
        stepNames: [],
        projectId: projectContext.state.model.Id,
        variableSet: projectVariableSet,
        libraryVariableSets,
        processIdentifier: templatedProjectIdentifier,
        refreshVariables,
        gitRef: gitRefResource,
        packages: {
            items: packageReferences,
            feeds,
            onRequestRefresh: async () => {
                await doBusyTask(async () => {
                    await refreshFeeds();
                });
            },
            // Process templates use this to set the action package property metadata.
            // This may not be relevant for project templates, or is at least something we'll have to handle as a special case.
            setPackages: () => noOp(),
        },
        actionTemplates: getAllProjectActionTemplates(projectContext.state.model, isFailedPermissionCheck(libraryVariableSets) ? [] : libraryVariableSets.map((lvs) => lvs.libraryVariableSet)),
        availableTargetTags: machineRoles,
        environments,
        workerPools: {
            items: parameterWorkerPools,
            onRequestRefresh: () => doBusyTask(async () => {
                const summaries = await repository.WorkerPools.summary();
                setParameterWorkerPools(summaries.WorkerPoolSummaries.map((s) => s.WorkerPool));
            }),
        },
        teams,
        channels,
        projects,
    };
    const requiredParameters = parameters.filter((p) => requiresValue(p, parameterValues, availableWorkerPools, isTenanted));
    const parameterValueRows = getParameterValueRows(requiredParameters, parameterValues, sourceItems);
    const sortedParameterValueRows = [...parameterValueRows.filter((row) => isAccountControlType(getControlTypeFromParameterValueRow(row))), ...parameterValueRows.filter((row) => !isAccountControlType(getControlTypeFromParameterValueRow(row)))];
    const parameterNamesInParameters = new Set(parameters.map((p) => p.Name));
    const parametersRemovedFromProjectTemplate = Object.keys(parameterValues).filter((key) => !parameterNamesInParameters.has(key));
    const clearValues = () => {
        parametersRemovedFromProjectTemplate.forEach((key) => {
            onParameterValueChanged(key, undefined);
        });
    };
    return (<>
            {requiredParameters.length > 0 ? (<>
                    {parametersRemovedFromProjectTemplate.length > 0 ? <ClearOldParameterValuesCallout onClearValues={clearValues}/> : null}
                    <ProjectTemplateParameterValuesTable parameterValueRows={sortedParameterValueRows} availableScopeValues={{ ...defaultScopeValues, Environments: environments }} platformHubAccounts={platformHubAccountNames} sourceItems={sourceItems} parameterValues={parameterValues} onParameterValueChanged={onParameterValueChanged} doBusyTask={doBusyTask} analyticsStepEditorDispatch={analyticsStepEditorDispatch}/>
                </>) : null}
        </>);
}
const requiresValue = (parameter: TemplateParameterResource, parameterValues: Record<string, PropertyValueResource | undefined>, availableWorkerPools: WorkerPoolResource[], isTenanted: boolean) => {
    if (parameter.DisplaySettings["Octopus.ControlType"] === ControlType.WorkerPool && availableWorkerPools.length === 0) {
        return false;
    }
    if (parameter.DisplaySettings["Octopus.ControlType"] === ControlType.TenantTags) {
        const value = convertPropertyValueResourceToString(parameterValues[parameter.Name]);
        if (!isTenanted && StringHelper.isNullOrWhiteSpace(value)) {
            return false;
        }
        return true;
    }
    return true;
};
interface ProjectTemplateParameterValuesTableProps {
    parameterValueRows: ParameterValueRow[];
    availableScopeValues: ScopeValues;
    platformHubAccounts: PlatformHubAccountSummary[];
    sourceItems: ProjectTemplateSourceItems;
    parameterValues: Record<string, PropertyValueResource | undefined>;
    onParameterValueChanged: (parameterName: string, value: PropertyValueResource | undefined) => void;
    doBusyTask: DoBusyTask;
    analyticsStepEditorDispatch: (name: string, event: StepEditorEvent) => void;
    hideScopeColumn?: boolean;
}
function ProjectTemplateParameterValuesTable({ parameterValueRows, availableScopeValues, platformHubAccounts, sourceItems, parameterValues, onParameterValueChanged, doBusyTask, analyticsStepEditorDispatch, hideScopeColumn, }: ProjectTemplateParameterValuesTableProps) {
    const { scopeHeaderRef, scopeWidth } = useScopeWidth();
    const [focusedValueKey, setFocusedValueKey] = useState<TemplateValueKey | null>(null);
    const nameColumnWidthAsPercentage = 26;
    const valueAndScopeColumnWidthAsPercentage = 37;
    return (<div>
            <GroupedDataTable data={parameterValueRows} columns={[
            {
                title: "Name",
                render: (data) => data.ValueIndex === 0 ? (<div>
                                    <div className={styles.parameterNameCellPrimaryHeader}>{data.Parameter.Label || data.Parameter.Name}</div>
                                    {data.Parameter.Label && <div className={styles.parameterNameCellSecondaryHeader}>{data.Parameter.Name}</div>}
                                    <div className={styles.parameterHelpText}>
                                        <Markdown markup={data.Parameter.HelpText} noMargins={true} mutedTextColor={true}/>
                                    </div>
                                </div>) : null,
                width: `${nameColumnWidthAsPercentage}%`,
                isChild: (data) => data.ValueIndex !== 0,
                padding: space[12],
                collapseNullCellsUpwards: true,
                verticalAlign: "top",
            },
            {
                title: "Value",
                render: (data) => (<ProjectTemplateConsumerParameterValueCell data={data} sourceItems={sourceItems} parameterValues={parameterValues} onParameterValueChanged={onParameterValueChanged} doBusyTask={doBusyTask} analyticsStepEditorDispatch={analyticsStepEditorDispatch} platformHubAccounts={platformHubAccounts} focusedValueKey={focusedValueKey} setFocusedValueKey={setFocusedValueKey}/>),
                width: hideScopeColumn ? `${2 * valueAndScopeColumnWidthAsPercentage}%` : `${valueAndScopeColumnWidthAsPercentage}%`,
                padding: "0",
            },
            ...(hideScopeColumn
                ? []
                : [
                    {
                        title: "Scope",
                        render: (data: ParameterValueRow) => {
                            const controlType = data.Parameter.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText;
                            const getScopeCellContents = () => {
                                if (!data.Value || !data.Value.Scope) {
                                    return null;
                                }
                                if (getUniqueAccountVariableNames(sourceItems).includes(data.Value.Value)) {
                                    return <div className={cx(styles.disabledText, styles.valueContainer)}>Defined by variable</div>;
                                }
                                return (<TemplateParameterScopeCell parameter={data.Parameter} value={data.Value} onChange={noOp} index={data.ValueIndex} isEditing={false} focusedValueKey={focusedValueKey} setFocusedValueKey={setFocusedValueKey} scopeValues={availableScopeValues || { Actions: [], Channels: [], Environments: [], Machines: [], Roles: [], TenantTags: [], Processes: [] }} height={48} width={scopeWidth} isConsumer={true}/>);
                            };
                            return (<div>
                                              {showMultipleValuesAndScopesIndicators(controlType, data.ValueIndex, data.Value) && (<div className={styles.multipleValuesAndScopesIndicator}>
                                                      <div>Multiple scopes</div>
                                                  </div>)}
                                              <div className={styles.scopeContainer}>{getScopeCellContents()}</div>
                                          </div>);
                        },
                        width: `${valueAndScopeColumnWidthAsPercentage}%`,
                        padding: "0",
                        ref: scopeHeaderRef,
                    },
                ]),
        ]} getRowKey={(data) => `${data.Parameter.Name}-${data.ValueIndex}`}/>
        </div>);
}
interface ProjectTemplateParameterProps {
    parameter: TemplateParameterResource;
    sourceItems: ProjectTemplateSourceItems;
    parameterValues: Record<string, PropertyValueResource | undefined>;
    onParameterValueChanged: (parameterName: string, value: PropertyValueResource | undefined) => void;
    doBusyTask: DoBusyTask;
    analyticsStepEditorDispatch: (name: string, event: StepEditorEvent) => void;
    autoFocus: boolean;
}
function ProjectTemplateParameter({ parameter, sourceItems, parameterValues, onParameterValueChanged, doBusyTask, analyticsStepEditorDispatch, autoFocus }: ProjectTemplateParameterProps) {
    const type = parameter.DisplaySettings["Octopus.ControlType"];
    const value = parameterValues[parameter.Name];
    // We do not have a process to use for error handling here. This may need some more consideration in the future.
    //const processErrorSelectors = useProcessErrorSelectors();
    //const getStepFieldError = (value: string) => processErrorSelectors.getStepFieldError(stepId, value);
    return type ? (<div className={styles.parameterContainer}>
            <ActionTemplateParameterInput parameter={parameter} sourceItems={sourceItems} doBusyTask={doBusyTask} value={value} onChange={(value) => onParameterValueChanged(parameter.Name, value)} actionType="Octopus.ProjectTemplate" 
    //error={getStepFieldError(parameter.Name)}
    analyticsStepEditorDispatch={analyticsStepEditorDispatch} autoFocus={autoFocus}/>
        </div>) : null;
}
const styles = templateParameterValueStyles;
function getParameterValueRows(parameters: TemplateParameterResource[], parameterValues: Record<string, PropertyValueResource | undefined>, sourceItems: ProjectTemplateSourceItems): ParameterValueRow[] {
    return parameters.flatMap((p, parameterIndex): ParameterValueRow[] => {
        let parameterWithOverrides = { ...p };
        let overrideVariableRow: ParameterValueRow | undefined = undefined;
        let startingValueIndex = 0;
        const controlType = p.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText;
        const overrideVariableName = parameterValues[p.Name];
        if (!!parameterValues[p.Name] && typeof overrideVariableName === "string" && isAccountControlType(controlType)) {
            overrideVariableRow = {
                Parameter: p,
                Value: { Value: overrideVariableName, Scope: {}, OverriddenScope: {} },
                ParameterIndex: parameterIndex,
                ValueIndex: startingValueIndex,
            };
            const completeOverrideVariableEnvironmentScope = getAllVariables(sourceItems)
                .filter((variable) => variable.Name === overrideVariableName)
                .flatMap((variable) => variable.Scope?.Environment?.filter((e) => e !== undefined) ?? []);
            parameterWithOverrides = {
                ...p,
                Values: p.Values.map((v) => ({
                    ...v,
                    OverriddenScope: { Environment: completeOverrideVariableEnvironmentScope },
                    OverrideValue: overrideVariableName,
                })),
            };
            startingValueIndex = 1;
        }
        else if (p.Values.length === 0) {
            return [{ Parameter: p, Value: null, ParameterIndex: parameterIndex, ValueIndex: 0 }];
        }
        return [
            ...(overrideVariableRow ? [overrideVariableRow] : []),
            ...parameterWithOverrides.Values.map<ParameterValueRow>((v, valueIndex) => ({ Parameter: parameterWithOverrides, Value: v, ParameterIndex: parameterIndex, ValueIndex: startingValueIndex + valueIndex })),
        ];
    });
}
interface ProjectTemplateConsumerParameterValueCellProps {
    data: ParameterValueRow;
    sourceItems: ProjectTemplateSourceItems;
    parameterValues: Record<string, PropertyValueResource | undefined>;
    onParameterValueChanged: (parameterName: string, value: PropertyValueResource | undefined) => void;
    doBusyTask: DoBusyTask;
    analyticsStepEditorDispatch: (name: string, event: StepEditorEvent) => void;
    platformHubAccounts: PlatformHubAccountSummary[];
    focusedValueKey: TemplateValueKey | null;
    setFocusedValueKey: (key: TemplateValueKey | null) => void;
}
function ProjectTemplateConsumerParameterValueCell(props: ProjectTemplateConsumerParameterValueCellProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const valueKey = createTemplateValueKey(props.data.Parameter.Name, props.data.ValueIndex, "Value");
    const onFocus = () => props.setFocusedValueKey(valueKey);
    const onBlur = () => props.setFocusedValueKey(null);
    const controlType = props.data.Parameter.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText;
    const isFocused = props.focusedValueKey === valueKey;
    const rowCanBeFocused = !isAccountControlType(controlType) || isAccountControlTypeWithoutDefaultValues(props.data) || isOverrideVariableRow(props.data, props.platformHubAccounts);
    const rowIsFocused = isFocused && rowCanBeFocused;
    const hasValue = () => props.parameterValues[props.data.Parameter.Name] || props.data.Value;
    const getValue = () => {
        if (props.parameterValues[props.data.Parameter.Name] && (!isAccountControlType(controlType) || isAccountControlTypeWithoutDefaultValues(props.data))) {
            return props.parameterValues[props.data.Parameter.Name];
        }
        else if (props.data.Value) {
            return props.data.Value?.Value;
        }
        return undefined;
    };
    const tooltipContent = isOverrideVariableRow(props.data, props.platformHubAccounts) ? "You can only provide a single value" : "Override with a project variable";
    const renderValue = () => {
        if (isAccountControlType(controlType) || controlType === ControlType.Sensitive) {
            return (<ReadonlyInlineVariableValue value={getValue()} variableType={getVariableTypeFromDisplaySettings(props.data.Parameter.DisplaySettings["Octopus.ControlType"])} defaultValue={undefined} accounts={getAccounts(props.platformHubAccounts, props.sourceItems)} certificates={{}} workerPools={{}} hasEditPermissions={rowCanBeFocused} sourceItems={undefined}/>);
        }
        const label = getLabel(props.data.Parameter);
        const options = selectOptionsToItems(props.data.Parameter.DisplaySettings["Octopus.SelectOptions"]);
        const packageParameterIsUsed = !!props.sourceItems.packages.items.find((p) => p.Properties["PackageParameterName"] === props.data.Parameter.Name);
        const value = props.parameterValues[props.data.Parameter.Name] || props.data.Value?.Value;
        const summary = createSummaryForParameterValue(label, controlType, options, props.sourceItems.packages.feeds, props.sourceItems.environments, props.sourceItems.workerPools.items, props.sourceItems.teams, props.sourceItems.channels, props.sourceItems.projects, packageParameterIsUsed)(value);
        return renderSummaryNodeForParameterValue(summary.node);
    };
    return (<div ref={containerRef}>
            {showMultipleValuesAndScopesIndicators(controlType, props.data.ValueIndex, props.data.Value) && (<div className={styles.multipleValuesAndScopesIndicator}>
                    <div>Multiple values</div>
                    {isAccountControlType(controlType) && (<div className={styles.overrideButtonContainer}>
                            <PopoverBasicWithTrigger description={<div className={styles.popoverBasicWithTriggerContainer} style={{ width: containerRef.current ? `${containerRef.current.offsetWidth - 24}px` : space[24] }}>
                                        <ProjectTemplateParameter key={props.data.Parameter.Name} parameter={props.data.Parameter} sourceItems={props.sourceItems} parameterValues={props.parameterValues} onParameterValueChanged={props.onParameterValueChanged} doBusyTask={props.doBusyTask} analyticsStepEditorDispatch={props.analyticsStepEditorDispatch} autoFocus={rowIsFocused}/>
                                    </div>} placement="top-end" width="extra-wide" triggerElement={<IconButtonWithTooltip toolTipContent={tooltipContent} customIcon={<PlusIcon size={20} color={isOverrideVariableRow(props.data, props.platformHubAccounts) ? themeTokens.color.icon.disabled : themeTokens.color.icon.secondary}/>} accessibleName={tooltipContent}/>} triggerAlignment="center" open={isOverrideVariableRow(props.data, props.platformHubAccounts) ? false : undefined} contentPadding={0}/>
                        </div>)}
                </div>)}
            <div onFocus={onFocus} onClick={onFocus} className={styles.valueContainer}>
                <PopoverWhenFocused isFocused={rowIsFocused} onClickOutside={onBlur} isContentCentered={true} isFullWidth={true}>
                    {rowIsFocused ? (<div className={styles.popoverWhenFocusedContainer}>
                            <ProjectTemplateParameter key={props.data.Parameter.Name} parameter={props.data.Parameter} sourceItems={props.sourceItems} parameterValues={props.parameterValues} onParameterValueChanged={props.onParameterValueChanged} doBusyTask={props.doBusyTask} analyticsStepEditorDispatch={props.analyticsStepEditorDispatch} autoFocus={rowIsFocused}/>
                        </div>) : hasValue() ? (renderValue()) : (<div className={styles.noValue}>
                            <MissingVariablesIcon show={true}/>
                            <Text value={""} placeholder={"Provide value"} onChange={noOp} hideUnderline={true} monoSpacedFont={true}/>
                        </div>)}
                </PopoverWhenFocused>
            </div>
        </div>);
}
